/**
 * "Search" style tile. Not that it isn't just used on the search results page,
 * but also search-ish places such as destination product pages.
 */

import classnames from 'clsx'
import React, { forwardRef, useCallback, useContext, useEffect } from 'react'
import styled from 'styled-components'

import MainOfferTile from '../MainOfferTile'
import SearchBedbankOffer from './SearchBedbankOffer'
import SearchBundleOffer from './SearchBundleOfferTile'
import SearchFlashOffer from './SearchFlashOffer'
import SearchOfferTile from './SearchOfferTile'

import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
} from 'constants/offer'
import noop from 'lib/noop'
import { scheduleIsCurrent } from 'lib/scheduleStatus'

import * as Analytics from 'analytics/analytics'
import { offerWithDepositTagClick, offerWithZeroReservationTileClick } from 'analytics/eventDefinitions'
import { searchClickLpcOffer, searchClickOffer, searchClickBedbankOffer, searchClickBedbankOfferAnytimeWithSell } from 'analytics/optimizely/events'
import { fireABTestGAEvent, fireInteractionEvent } from 'api/googleTagManager'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CruiseSearchTile from 'components/Cruises/CruisesTile/SearchPage/CruiseSearchTile'
import config from 'constants/config'
import PrimaryButtonPaletteProvider from 'contexts/PrimaryButtonPaletteProvider'
import DepositTagOffersContext from 'contexts/depositTagOffersContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { isBundleOffer, isCruiseOffer, isCruiseV1Offer, isTourV2Offer } from 'lib/offerTypes'
import CruiseV1SearchTile from '../CruiseV1SearchTile'
import { TopLevelTileProps } from '../OfferTileTypes'
import TourV2SearchTile from '../TourV2SearchTile'
import SearchRentalTile from './SearchRentalTile/SearchRentalTile'
import { hasPassedWalledGarden } from 'selectors/accountSelectors'
import { connect } from 'react-redux'

const OfferLink = styled(ClickableLink)`
  position: relative;
  display: block;
  user-select: none;

  &,
  &:hover,
  &:focus {
    color: ${props => props.theme.palette.neutral.one};
    text-decoration: none;
  }
`

function getTestId(offer: App.AnyOffer) {
  let flightsEnabled
  let onlinePurchaseSchedule

  if (offer.type !== OFFER_TYPE_BED_BANK) {
    const internalOffer = offer as App.Offer

    flightsEnabled = internalOffer.offerFlightsEnabled
    onlinePurchaseSchedule = internalOffer.onlinePurchaseSchedule
  }

  return classnames(
    `offer-${offer.type}`, {
      'offer-with-flights': flightsEnabled,
      [`Purchasable-${offer.type}`]: scheduleIsCurrent(onlinePurchaseSchedule),
    })
}

interface Props extends TopLevelTileProps {
  className?: string;
  position?: number;
  hasPassedWalledGarden: boolean;
}

const SearchTile = forwardRef<HTMLAnchorElement, Props>((props, ref) => {
  const {
    offer,
    filters,
    productClick,
    eagerLoadFirstImage,
    offerLinkIncludesFilters,
    className,
    position,
    hasPassedWalledGarden,
  } = props
  const {
    hasDepositTagOffer = () => false,
    clearDepositTagOffers = noop,
  } = useContext(DepositTagOffersContext) ?? {}

  useEffect(() => {
    return () => clearDepositTagOffers()
  }, [clearDepositTagOffers])
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const offerUrl = useOfferUrl(offer, {
    filters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters,
  })

  const handleClick = useCallback(() => {
    Analytics.track('search-click-offer', searchClickOffer())
    if (offer.type === OFFER_TYPE_HOTEL && offer.reserveForZero) {
      fireInteractionEvent(offerWithZeroReservationTileClick())
    } else if (hasDepositTagOffer(offer.id)) {
      fireInteractionEvent(offerWithDepositTagClick())
    }

    if (offer.type === OFFER_TYPE_ALWAYS_ON) {
      Analytics.track('search-click-lpc-offer', searchClickLpcOffer())
      fireABTestGAEvent({
        category: 'lpc-search-tile',
        action: 'click',
      }, false)
    }

    if (offer.type === OFFER_TYPE_BED_BANK) {
      Analytics.track('search-click-bedbank-offer', searchClickBedbankOffer())
      fireABTestGAEvent({
        category: 'bedbank-search-tile',
        action: 'click',
      }, false)
    }

    if (offer.type === OFFER_TYPE_BED_BANK && offer.sell && !filters?.checkIn && !filters?.checkOut && !filters?.flexibleMonths && !filters?.flexibleNights && hasPassedWalledGarden) {
      Analytics.track('search-click-bedbank-offer-anytime-with-sell', searchClickBedbankOfferAnytimeWithSell())
    }

    productClick?.(offer)
  }, [offer, hasDepositTagOffer, filters?.checkIn, filters?.checkOut, filters?.flexibleMonths, filters?.flexibleNights, hasPassedWalledGarden, productClick])

  const linkParams = {
    onClick: handleClick,
    to: offerUrl,
    'data-testid': getTestId(offer),
    ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
  }

  if (isBundleOffer(offer) && !config.ENABLE_BUNDLE_AND_SAVE) {
    return null
  }

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <OfferLink {...linkParams} className={className} ref={ref} >
        {offer.type === OFFER_TYPE_HOTEL &&
          <PrimaryButtonPaletteProvider palette={offer.property?.isUltraLux ? 'ultralux' : 'default'}>
            <SearchFlashOffer
              offer={offer as App.Offer | App.OfferSummary}
              filters={filters}
              offerMetaData={offerMetaData}
              eagerLoadFirstImage={eagerLoadFirstImage}
              offerUrl={offerUrl}
              offerLinkIncludesFilters={offerLinkIncludesFilters}
            />
          </PrimaryButtonPaletteProvider>}
        {isBundleOffer(offer) &&
          <SearchBundleOffer
            offer={offer as App.BundleOffer}
            filters={filters}
            offerMetaData={offerMetaData}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
          />}
        {isCruiseV1Offer(offer) &&
          <CruiseV1SearchTile
            offer={offer as App.Offer | App.OfferSummary}
            offerUrl={offerUrl}
            position={position}
          />}
        {!isCruiseV1Offer(offer) && offer.type === OFFER_TYPE_TOUR &&
          <MainOfferTile
            offer={offer as App.Offer | App.OfferSummary}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
          />}
        {isTourV2Offer(offer) && <TourV2SearchTile
          offer={offer}
        />}
        {(offer.type === OFFER_TYPE_LAST_MINUTE || offer.type === OFFER_TYPE_ALWAYS_ON) &&
          <PrimaryButtonPaletteProvider palette={offer.property?.isUltraLux ? 'ultralux' : 'default'}>
            <SearchOfferTile
              offer={offer as App.Offer | App.OfferSummary}
              filters={filters}
              offerMetaData={offerMetaData}
              eagerLoadFirstImage={eagerLoadFirstImage}
              offerUrl={offerUrl}
              offerLinkIncludesFilters={offerLinkIncludesFilters}
            />
          </PrimaryButtonPaletteProvider>}
        {offer.type === OFFER_TYPE_BED_BANK &&
          <SearchBedbankOffer
            offer={offer as App.BedbankOffer | App.BedbankOfferSummary}
            filters={filters}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
            offerMetaData={offerMetaData}
          />
        }
        {isCruiseOffer(offer) &&
          <CruiseSearchTile
            cruise={offer}
          />
        }
        {offer.type === 'rental' &&
          <SearchRentalTile
            offer={offer as App.Offer}
            filters={filters}
            offerUrl={offerUrl}
          />
        }
      </OfferLink>
    </ErrorBoundary>
  )
})

SearchTile.defaultProps = {
  productClick: noop,
  offerLinkIncludesFilters: true,
}

const mapStateToProps = (state: App.State) => ({
  hasPassedWalledGarden: hasPassedWalledGarden(state),
})

export default connect(mapStateToProps)(SearchTile)
